/**
 * Combined week / month / quarter period picker.
 *
 * Week view is a day-picker-style calendar whose hit target is the ISO week
 * row (Mon–Sun), not a single day. Month and quarter are grids — a day
 * calendar is the wrong metaphor for those grains.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { CalendarIcon, CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import Popover from 'src/components/ui/Popover';
import {
  addIsoWeeks,
  formatIsoWeekLabel,
  formatIsoWeekRangeLabel,
  getIsoWeek,
  isoWeekDateRange,
  isoWeekOverlapsMonth,
  isoWeekToCalendarMonth,
  isoYearForCalendarMonth,
  lastIsoWeekOfMonth,
  londonToday,
  weeksOverlappingMonth
} from '../utils/isoWeek';
import {
  FISCAL_QUARTERS,
  MONTHS,
  type PeriodGrain,
  formatPeriodLabel,
  getFiscalQuarterFromMonth,
  representativeMonthForQuarter,
  stepCalendarMonth,
  stepFiscalQuarter
} from '../utils/constants';

export interface PeriodValue {
  grain: PeriodGrain;
  year: number;
  month: number;
  week: number | null;
}

interface PeriodPickerProps {
  grain: PeriodGrain;
  year: number;
  month: number;
  week: number | null;
  onChange: (next: PeriodValue) => void;
  label?: string;
  className?: string;
}

const GRAINS: { value: PeriodGrain; label: string }[] = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' }
];

const MONTH_SHORT = MONTHS.map((name) => name.slice(0, 3));
const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function triggerLabel(grain: PeriodGrain, year: number, month: number, week: number | null): string {
  if (grain === 'week') {
    const isoWeek = week ?? lastIsoWeekOfMonth(year, month).isoWeek;
    const isoYear = isoYearForCalendarMonth(year, month, isoWeek);
    return `${formatIsoWeekLabel(isoYear, isoWeek)} · ${formatIsoWeekRangeLabel(isoYear, isoWeek)}`;
  }
  if (grain === 'quarter') {
    return formatPeriodLabel(year, month, 'quarterly');
  }
  return formatPeriodLabel(year, month, 'monthly');
}

function stepPeriod(value: PeriodValue, delta: -1 | 1): PeriodValue {
  if (value.grain === 'week') {
    const isoWeek = value.week ?? lastIsoWeekOfMonth(value.year, value.month).isoWeek;
    const isoYear = isoYearForCalendarMonth(value.year, value.month, isoWeek);
    const next = addIsoWeeks(isoYear, isoWeek, delta);
    if (isoWeekOverlapsMonth(next.isoYear, next.isoWeek, value.year, value.month)) {
      return { ...value, week: next.isoWeek };
    }
    const cal = isoWeekToCalendarMonth(next.isoYear, next.isoWeek);
    return { grain: 'week', year: cal.year, month: cal.month, week: next.isoWeek };
  }
  if (value.grain === 'quarter') {
    const next = stepFiscalQuarter(value.year, value.month, delta);
    return { grain: 'quarter', year: next.year, month: next.month, week: null };
  }
  const next = stepCalendarMonth(value.year, value.month, delta);
  return { grain: 'month', year: next.year, month: next.month, week: null };
}

export default function PeriodPicker({ grain, year, month, week, onChange, label, className = '' }: PeriodPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(year);
  const [viewMonth, setViewMonth] = useState(month);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerId = useId();
  const labelId = useId();
  const valueId = useId();
  const value: PeriodValue = { grain, year, month, week };

  useEffect(() => {
    if (isOpen) {
      setViewYear(year);
      setViewMonth(month);
    }
  }, [isOpen, year, month]);

  const commit = (next: PeriodValue) => {
    onChange(next);
    setIsOpen(false);
  };

  const handleGrainChange = (nextGrain: PeriodGrain) => {
    if (nextGrain === grain) return;
    if (nextGrain === 'week') {
      const isoWeek = week ?? lastIsoWeekOfMonth(year, month).isoWeek;
      onChange({ grain: 'week', year, month, week: isoWeek });
      return;
    }
    if (nextGrain === 'quarter') {
      const q = getFiscalQuarterFromMonth(month);
      onChange({ grain: 'quarter', year, month: representativeMonthForQuarter(q), week: null });
      return;
    }
    onChange({ grain: 'month', year, month, week: null });
  };

  return (
    <div className={`inline-block ${className}`}>
      {label && (
        <label id={labelId} htmlFor={triggerId} className="block text-sm font-semibold text-content-subtle mb-1">
          {label}
        </label>
      )}
      <div
        ref={anchorRef}
        className="flex h-9 items-center gap-0.5 px-0.5 text-sm border-2 border-border-default rounded-lg bg-surface box-border"
      >
        <button
          type="button"
          onClick={() => onChange(stepPeriod(value, -1))}
          className="inline-flex h-full w-7 items-center justify-center hover:bg-surface-subtle rounded-md transition-colors"
          aria-label={`Previous ${grain}`}
        >
          <CaretLeftIcon size={16} className="text-content-subtle" />
        </button>
        <button
          id={triggerId}
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-labelledby={label ? `${labelId} ${valueId}` : valueId}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          className="flex h-full items-center gap-2 px-2 hover:bg-surface-subtle rounded-md transition-colors min-w-[168px] justify-between"
        >
          <span id={valueId} className="text-left leading-tight">
            {triggerLabel(grain, year, month, week)}
          </span>
          <CalendarIcon size={16} className="text-content-subtle shrink-0" />
        </button>
        <button
          type="button"
          onClick={() => onChange(stepPeriod(value, 1))}
          className="inline-flex h-full w-7 items-center justify-center hover:bg-surface-subtle rounded-md transition-colors"
          aria-label={`Next ${grain}`}
        >
          <CaretRightIcon size={16} className="text-content-subtle" />
        </button>
      </div>

      <Popover
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={() => setIsOpen(false)}
        placement="bottom-end"
        offset={[0, 8]}
        className="p-3 w-[320px]"
      >
        <div role="dialog" aria-label="Select reporting period">
          <div className="flex rounded-lg bg-surface-subtle p-0.5 mb-3" role="tablist" aria-label="Period grain">
            {GRAINS.map((option) => {
              const selected = option.value === grain;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => handleGrainChange(option.value)}
                  className={`flex-1 px-2 py-1 text-xs rounded-md transition-colors ${
                    selected
                      ? 'bg-surface text-content-default font-medium shadow-sm'
                      : 'text-content-subtle hover:text-content-default'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          {grain === 'week' && (
            <WeekPanel
              viewYear={viewYear}
              viewMonth={viewMonth}
              selectedYear={year}
              selectedMonth={month}
              selectedWeek={week ?? lastIsoWeekOfMonth(year, month).isoWeek}
              onViewChange={(nextYear, nextMonth) => {
                setViewYear(nextYear);
                setViewMonth(nextMonth);
              }}
              onSelectWeek={(isoYear, isoWeek) => {
                const cal = isoWeekToCalendarMonth(isoYear, isoWeek);
                const monthForWeek = isoWeekOverlapsMonth(isoYear, isoWeek, viewYear, viewMonth)
                  ? { year: viewYear, month: viewMonth }
                  : cal;
                commit({ grain: 'week', year: monthForWeek.year, month: monthForWeek.month, week: isoWeek });
              }}
            />
          )}

          {grain === 'month' && (
            <MonthPanel
              viewYear={viewYear}
              selectedYear={year}
              selectedMonth={month}
              onViewYearChange={setViewYear}
              onSelect={(nextYear, nextMonth) =>
                commit({ grain: 'month', year: nextYear, month: nextMonth, week: null })
              }
            />
          )}

          {grain === 'quarter' && (
            <QuarterPanel
              viewYear={viewYear}
              selectedYear={year}
              selectedMonth={month}
              onViewYearChange={setViewYear}
              onSelect={(nextYear, nextMonth) =>
                commit({ grain: 'quarter', year: nextYear, month: nextMonth, week: null })
              }
            />
          )}
        </div>
      </Popover>
    </div>
  );
}

function WeekPanel({
  viewYear,
  viewMonth,
  selectedYear,
  selectedMonth,
  selectedWeek,
  onViewChange,
  onSelectWeek
}: {
  viewYear: number;
  viewMonth: number;
  selectedYear: number;
  selectedMonth: number;
  selectedWeek: number;
  onViewChange: (year: number, month: number) => void;
  onSelectWeek: (isoYear: number, isoWeek: number) => void;
}) {
  const weeks = weeksOverlappingMonth(viewYear, viewMonth);
  const selectedIsoYear = isoYearForCalendarMonth(selectedYear, selectedMonth, selectedWeek);
  const today = getIsoWeek(londonToday());

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => {
            const next = stepCalendarMonth(viewYear, viewMonth, -1);
            onViewChange(next.year, next.month);
          }}
          className="p-1 hover:bg-surface-subtle rounded"
          aria-label="Previous month"
        >
          <CaretLeftIcon size={16} className="text-content-subtle" />
        </button>
        <span className="text-sm font-semibold">
          {MONTHS[viewMonth - 1]} {viewYear}
        </span>
        <button
          type="button"
          onClick={() => {
            const next = stepCalendarMonth(viewYear, viewMonth, 1);
            onViewChange(next.year, next.month);
          }}
          className="p-1 hover:bg-surface-subtle rounded"
          aria-label="Next month"
        >
          <CaretRightIcon size={16} className="text-content-subtle" />
        </button>
      </div>
      <div className="grid grid-cols-[2rem_repeat(7,minmax(0,1fr))] gap-0.5 text-[11px] text-content-subtle mb-1 px-1">
        <span className="text-center">Wk</span>
        {WEEKDAY_HEADERS.map((day) => (
          <span key={day} className="text-center">
            {day}
          </span>
        ))}
      </div>
      <div className="flex flex-col gap-0.5">
        {weeks.map((iso) => {
          const [monday] = isoWeekDateRange(iso.isoYear, iso.isoWeek);
          const isSelected = iso.isoYear === selectedIsoYear && iso.isoWeek === selectedWeek;
          const isCurrent = iso.isoYear === today.isoYear && iso.isoWeek === today.isoWeek;
          return (
            <button
              key={`${iso.isoYear}-${iso.isoWeek}`}
              type="button"
              onClick={() => onSelectWeek(iso.isoYear, iso.isoWeek)}
              className={`grid grid-cols-[2rem_repeat(7,minmax(0,1fr))] gap-0.5 items-center rounded-md px-1 py-0.5 text-xs transition-colors ${
                isSelected
                  ? 'bg-secondary-blue text-white'
                  : isCurrent
                    ? 'bg-blue-50 text-secondary-blue hover:bg-blue-100'
                    : 'hover:bg-surface-subtle text-content-default'
              }`}
              aria-label={`${formatIsoWeekLabel(iso.isoYear, iso.isoWeek)} ${formatIsoWeekRangeLabel(iso.isoYear, iso.isoWeek)}`}
            >
              <span className={`text-center font-medium ${isSelected ? 'text-white' : 'text-content-subtle'}`}>
                {iso.isoWeek}
              </span>
              {Array.from({ length: 7 }, (_, i) => {
                const day = new Date(monday);
                day.setUTCDate(monday.getUTCDate() + i);
                const inMonth = day.getUTCFullYear() === viewYear && day.getUTCMonth() + 1 === viewMonth;
                return (
                  <span
                    key={i}
                    className={`text-center ${isSelected ? 'text-white' : inMonth ? '' : 'text-content-subtlest'}`}
                  >
                    {day.getUTCDate()}
                  </span>
                );
              })}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-content-subtle">Click a week row — days are shown for context only.</p>
    </div>
  );
}

function MonthPanel({
  viewYear,
  selectedYear,
  selectedMonth,
  onViewYearChange,
  onSelect
}: {
  viewYear: number;
  selectedYear: number;
  selectedMonth: number;
  onViewYearChange: (year: number) => void;
  onSelect: (year: number, month: number) => void;
}) {
  const now = new Date();
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => onViewYearChange(viewYear - 1)}
          className="p-1 hover:bg-surface-subtle rounded"
          aria-label="Previous year"
        >
          <CaretLeftIcon size={16} className="text-content-subtle" />
        </button>
        <span className="text-sm font-semibold">{viewYear}</span>
        <button
          type="button"
          onClick={() => onViewYearChange(viewYear + 1)}
          className="p-1 hover:bg-surface-subtle rounded"
          aria-label="Next year"
        >
          <CaretRightIcon size={16} className="text-content-subtle" />
        </button>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {MONTH_SHORT.map((name, index) => {
          const monthNum = index + 1;
          const isSelected = viewYear === selectedYear && monthNum === selectedMonth;
          const isCurrent = viewYear === now.getFullYear() && monthNum === now.getMonth() + 1;
          return (
            <button
              key={name}
              type="button"
              onClick={() => onSelect(viewYear, monthNum)}
              className={`px-2 py-1.5 text-sm rounded transition-colors ${
                isSelected
                  ? 'bg-secondary-blue text-white font-medium'
                  : isCurrent
                    ? 'bg-blue-50 text-secondary-blue hover:bg-blue-100'
                    : 'text-content-default hover:bg-surface-subtle'
              }`}
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function QuarterPanel({
  viewYear,
  selectedYear,
  selectedMonth,
  onViewYearChange,
  onSelect
}: {
  viewYear: number;
  selectedYear: number;
  selectedMonth: number;
  onViewYearChange: (year: number) => void;
  onSelect: (year: number, month: number) => void;
}) {
  const selectedQuarter = getFiscalQuarterFromMonth(selectedMonth);
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => onViewYearChange(viewYear - 1)}
          className="p-1 hover:bg-surface-subtle rounded"
          aria-label="Previous year"
        >
          <CaretLeftIcon size={16} className="text-content-subtle" />
        </button>
        <span className="text-sm font-semibold">{viewYear}</span>
        <button
          type="button"
          onClick={() => onViewYearChange(viewYear + 1)}
          className="p-1 hover:bg-surface-subtle rounded"
          aria-label="Next year"
        >
          <CaretRightIcon size={16} className="text-content-subtle" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1">
        {FISCAL_QUARTERS.map((q) => {
          const isSelected = viewYear === selectedYear && selectedQuarter === q.value;
          const label = formatPeriodLabel(viewYear, q.representativeMonth, 'quarterly');
          return (
            <button
              key={q.value}
              type="button"
              onClick={() => onSelect(viewYear, q.representativeMonth)}
              className={`px-2 py-2 text-sm rounded text-left transition-colors ${
                isSelected ? 'bg-secondary-blue text-white font-medium' : 'text-content-default hover:bg-surface-subtle'
              }`}
            >
              <div>{label}</div>
              <div className={`text-[11px] ${isSelected ? 'text-white/80' : 'text-content-subtle'}`}>
                {q.rangeLabel}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
